/**
 * <EnrollmentForm> is the ONE enrollment form for every supplier, state and plan
 * (blueprint decision D2, Product Contract 5). It renders the fixed EnrollmentCore
 * fields and appends the connector's declared `extensionFields`. It must never fork
 * per supplier: switching supplier changes ONLY `extensionFields`, never the layout,
 * steps, or component. If a supplier needs something the core does not have, add an
 * extension field on that connector, not a new form.
 *
 * Correctness at the client boundary:
 *  - One submission_idempotency_key per mounted form, reused on every retry.
 *  - Submit is locked while in flight (ref guard, not just a disabled button).
 *    The server's unique index on the key is the real protection.
 *  - A timeout / dropped connection is INDETERMINATE, not "failed": the copy says the
 *    request may have been received and a same-key resend is safe.
 *  - Consents are three separate unchecked checkboxes (tos, data-share required; sms
 *    optional) using versioned text from lib/consentTexts.js. The rendered text and
 *    version id are recorded in the payload.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { ArrowRight } from "lucide-react";
import { renderConsent, ACK_TEXTS } from "@/lib/consentTexts";
import { esidFieldHelp, isEsidField, WHAT_HAPPENS_WHEN_YOU_ENROLL_LEAD } from "@/lib/disclosureTexts";
import CompensationDisclosure from "@/components/CompensationDisclosure";
import DocumentLinks from "@/components/DocumentLinks";
import ErrorSummary from "@/components/ErrorSummary";

// DOM id for each EnrollmentCore / consent field, keyed by its react-hook-form register name. Used only to
// build the error summary's jump links; every Field below still owns its own id and inline error.
const FIELD_DOM_ID = {
  first_name: "enr-first_name", last_name: "enr-last_name", email: "enr-email", phone: "enr-phone",
  "service_address.street": "enr-street", "service_address.unit": "enr-unit", "service_address.city": "enr-city", "service_address.zip": "enr-zip",
  preferred_start_date: "enr-start",
  consent_tos: "enr-consent_tos", consent_data_share: "enr-consent_data_share", consent_authority: "enr-consent_authority",
  consent_docs: "enr-consent_docs", consent_rate: "enr-consent_rate",
};
const FIELD_LABEL = {
  first_name: "First name", last_name: "Last name", email: "Email", phone: "Phone",
  "service_address.street": "Street address", "service_address.unit": "Unit", "service_address.city": "City", "service_address.zip": "ZIP code",
  preferred_start_date: "Preferred start date",
  consent_tos: "Terms of Use agreement", consent_data_share: "Data-sharing authorization", consent_authority: "Authorization confirmation",
  consent_docs: "Plan documents acknowledgment", consent_rate: "Updated rate confirmation",
};

function domIdFor(path) {
  if (FIELD_DOM_ID[path]) return FIELD_DOM_ID[path];
  const m = /^supplier_extension\.(.+)$/.exec(path);
  return m ? `enr-ext-${m[1]}` : null;
}
function labelFor(path) {
  if (FIELD_LABEL[path]) return FIELD_LABEL[path];
  const m = /^supplier_extension\.(.+)$/.exec(path);
  return m ? m[1] : path;
}
/** react-hook-form's nested errors object (e.g. errors.service_address.street) flattened to dotted paths. */
function flattenFieldErrors(errObj, prefix = "") {
  const out = [];
  Object.entries(errObj || {}).forEach(([key, val]) => {
    if (!val) return;
    const path = prefix ? `${prefix}.${key}` : key;
    if (val.message) out.push(path);
    else if (typeof val === "object") out.push(...flattenFieldErrors(val, path));
  });
  return out;
}

// Extension fields must never collect a payment instrument (Product Contract 4).
// The backend registry rejects these; this is a defensive second check in the UI.
const PAYMENT_FIELD_DENYLIST = /card|cvv|cvc|routing|iban|bank|deposit|payment/i;

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// The start key must be a UUID (the server rejects anything else), so it never uses the idempotency-key fallback.
function newStartKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function Field({ id, label, error, hint, required, children }) {
  const errId = `${id}-error`;
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="text-overline text-zinc-500 mb-1 block">
        {label}
        {required ? <span aria-hidden="true"> *</span> : <span className="normal-case tracking-normal"> (optional)</span>}
      </label>
      {children({
        id,
        "aria-invalid": error ? "true" : "false",
        "aria-describedby": [error ? errId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined,
      })}
      {hint && <p id={hintId} className="text-xs text-zinc-500 mt-1">{hint}</p>}
      {error && (
        <p id={errId} role="alert" className="text-sm text-red-700 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}

function ConsentText({ rendered }) {
  return rendered.parts.map((p, i) =>
    p.link ? (
      <a key={i} href={p.link} target="_blank" rel="noreferrer" className="underline">{p.text}</a>
    ) : p.strong ? (
      <strong key={i}>{p.text}</strong>
    ) : (
      <span key={i}>{p.text}</span>
    )
  );
}

export default function EnrollmentForm({
  plan,
  provider,
  stateEntry,
  extensionFields = [],
  planRateSnapshotId,
  energyType = "electricity",
  customerType = "residential",
  commercialQuoteId = null,
  serverConfig = null, // real backend config: server-rendered consent texts, version ids, disclosure, document hashes
  onSubmit,
  onSubmitted,
  onRateChanged = null, // async () => void: reload the plan and config after the server says the rate changed (QA-17)
  onStart = null, // async ({ startKey, planId, stateCode, planRateSnapshotId }) => { start_key }: form-open record (reporting)
}) {
  const idempotencyKey = useRef(newIdempotencyKey());
  // One start key per form open. It rides along on the submission so the server can link the two.
  const startKey = useRef(newStartKey());
  const startSent = useRef(false);
  useEffect(() => {
    if (!onStart || startSent.current) return;
    startSent.current = true; // once per mounted form, even if the effect runs twice
    // Fire and forget: a failed or slow start record must never block or fail the form. The submission still
    // carries the key, and the server ignores a key it has no start for.
    Promise.resolve()
      .then(() => onStart({
        startKey: startKey.current,
        planId: plan.id,
        stateCode: stateEntry.code,
        planRateSnapshotId,
      }))
      .then((res) => { if (res && res.start_key) startKey.current = res.start_key; })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const inFlight = useRef(false);
  const displayedAt = useRef(new Date().toISOString());
  // After the server reports a changed or expired rate the customer must see the new rate and confirm it again.
  const [rateReview, setRateReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null); // { kind: 'indeterminate' | 'unavailable' | 'rejected' | 'validation', text }
  const [summaryErrors, setSummaryErrors] = useState([]);
  const summaryRef = useRef(null);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      service_address: { street: "", unit: "", city: "", zip: "" },
      preferred_start_date: "",
      supplier_extension: {},
      consent_tos: false,
      consent_data_share: false,
      consent_sms: false,
      consent_authority: false,
      consent_docs: false,
      consent_rate: false,
    },
  });

  const registeredName = stateEntry ? stateEntry.broker_registered_name : null;
  const consents = useMemo(() => {
    // With a real backend config, the SERVER's versioned, rendered text is the source of truth
    // (its version id is what the server validates). Without one (mock/dev) use the local drafts.
    const fromServer = (type, required) => {
      const t = serverConfig && serverConfig.consent_texts && serverConfig.consent_texts[type];
      return t ? { versionId: t.text_version_id, required, parts: [{ text: t.rendered_text }], plain: t.rendered_text } : null;
    };
    const local = (type) => renderConsent(type, { providerName: provider && provider.name, registeredName });
    return {
      tos: serverConfig ? fromServer("tos", true) : local("tos"),
      data_share: serverConfig ? fromServer("data_share", true) : local("data_share"),
      sms: serverConfig ? fromServer("sms", false) : local("sms"), // null when the server has no SMS text: block is hidden
    };
  }, [provider, registeredName, serverConfig]);

  const safeExtensionFields = extensionFields.filter((f) => {
    if (PAYMENT_FIELD_DENYLIST.test(f.name)) {
      // eslint-disable-next-line no-console
      console.error(`EnrollmentForm: refusing to render payment-like extension field "${f.name}"`);
      return false;
    }
    return true;
  });

  const onInvalid = (formErrors) => {
    const list = flattenFieldErrors(formErrors)
      .map((path) => ({ id: domIdFor(path), label: labelFor(path) }))
      .filter((e) => e.id);
    setSummaryErrors(list);
  };
  useEffect(() => {
    if (summaryErrors.length > 0 && summaryRef.current) summaryRef.current.focus();
  }, [summaryErrors]);

  const submit = async (values) => {
    if (inFlight.current) return; // double-click / double-Enter guard
    setSummaryErrors([]);
    inFlight.current = true;
    setSubmitting(true);
    setBanner(null);

    const now = new Date().toISOString();
    const events = [
      { type: "tos", checked: values.consent_tos },
      { type: "data_share", checked: values.consent_data_share },
      ...(consents.sms ? [{ type: "sms", checked: values.consent_sms }] : []),
    ].map(({ type, checked }) => ({
      consent_type: type,
      text_version_id: consents[type].versionId,
      rendered_text: consents[type].plain,
      provider_id: type === "data_share" ? provider.id : null,
      checkbox_state: !!checked,
      captured_at: now,
      page_url: typeof window !== "undefined" ? window.location.href : "",
    }));

    const supplierExtension = {};
    safeExtensionFields.forEach((f) => {
      supplierExtension[f.name] = (values.supplier_extension || {})[f.name] ?? "";
    });

    const payload = {
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      service_address: {
        street: values.service_address.street.trim(),
        unit: values.service_address.unit.trim(),
        city: values.service_address.city.trim(),
        state: stateEntry.code,
        zip: values.service_address.zip.trim(),
      },
      energy_type: energyType,
      customer_type: customerType,
      commercial_quote_id: commercialQuoteId,
      provider_id: provider.id,
      plan_id: plan.id,
      plan_rate_snapshot_id: planRateSnapshotId,
      preferred_start_date: values.preferred_start_date || null,
      consent: {
        tos_accepted_at: values.consent_tos ? now : null,
        data_share_consent_at: values.consent_data_share ? now : null,
        sms_consent_at: values.consent_sms ? now : null,
        authority_attested_at: values.consent_authority ? now : null,
        supplier_docs_acknowledged_at: values.consent_docs ? now : null,
      },
      displayed_at: displayedAt.current,
      disclosure_version_id: serverConfig && serverConfig.disclosure ? serverConfig.disclosure.version_id : null,
      privacy_version_id: serverConfig ? serverConfig.privacy_version_id : null,
      supplier_doc_hashes: serverConfig && serverConfig.supplier_documents && serverConfig.supplier_documents.efl
        ? [serverConfig.supplier_documents.efl.sha256] : [],
      consent_events: events,
      supplier_extension: supplierExtension,
      submission_idempotency_key: idempotencyKey.current,
      start_key: startKey.current,
    };

    try {
      const result = await onSubmit(payload);
      if (onSubmitted) onSubmitted(result, payload);
    } catch (err) {
      const kind = err && err.kind;
      if (kind === "validation") {
        Object.entries(err.fieldErrors || {}).forEach(([name, message]) => setError(name, { type: "server", message }));
        setBanner({ kind, text: "Please check the highlighted fields and try again." });
      } else if (kind === "indeterminate") {
        setBanner({
          kind,
          text: "We could not confirm whether your request was received. It may have gone through. Sending it again is safe: we will not create a duplicate.",
        });
      } else if (kind === "rate_changed") {
        // The rate the customer saw is no longer current. Reload the plan and config so the new rate and documents are
        // shown, then require an explicit re-confirmation. A fresh idempotency key and displayed-at time: this is a new
        // decision about a new rate, not a retry of the old one. The text and documents shown come from the reloaded config, so their agreements reset.
        let reloaded = true;
        try { if (onRateChanged) await onRateChanged(); } catch { reloaded = false; }
        if (reloaded) {
          idempotencyKey.current = newIdempotencyKey();
          displayedAt.current = new Date().toISOString();
          // The consent texts and documents are reloaded with the new rate, so every agreement tied to them is asked again.
          setValue("consent_tos", false);
          setValue("consent_data_share", false);
          setValue("consent_docs", false);
          setValue("consent_rate", false);
          setRateReview(true);
          setBanner({ kind, text: "The rate for this plan changed after you opened this page. Nothing was sent. The new rate is shown on this page. Please review it and confirm to continue." });
        } else {
          setBanner({ kind: "rate_reload_failed", text: "The rate for this plan changed, and we could not load the new rate. Nothing was sent. Reload this page and try again." });
        }
      } else if (kind === "rate_limited") {
        setBanner({ kind, text: "Too many attempts. Please try again in an hour." });
      } else if (kind === "unavailable") {
        setBanner({ kind, text: "Enrollment is not available right now. Nothing was sent. Please try again later." });
      } else {
        setBanner({ kind: "rejected", text: "We could not accept this request. Nothing further will happen until you try again." });
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const e = errors;
  const addr = e.service_address || {};
  const extErr = e.supplier_extension || {};
  const submitLabel = submitting
    ? "Sending…"
    : banner && banner.kind === "indeterminate"
    ? "Send again"
    : "Submit enrollment";

  return (
    <form onSubmit={handleSubmit(submit, onInvalid)} noValidate className="space-y-5" data-testid="enrollment-form" aria-label="Enrollment">
      <ErrorSummary ref={summaryRef} errors={summaryErrors} />
      {banner && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 text-red-800 p-4 text-sm" data-testid={`enroll-banner-${banner.kind}`}>
          {banner.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field id="enr-first_name" label="First name" required error={e.first_name && e.first_name.message}>
          {(a) => (
            <input {...a} type="text" autoComplete="given-name" className="input-brutal" data-testid="field-first_name"
              {...register("first_name", { required: "Enter your first name.", validate: (v) => v.trim() !== "" || "Enter your first name." })} />
          )}
        </Field>
        <Field id="enr-last_name" label="Last name" required error={e.last_name && e.last_name.message}>
          {(a) => (
            <input {...a} type="text" autoComplete="family-name" className="input-brutal" data-testid="field-last_name"
              {...register("last_name", { required: "Enter your last name.", validate: (v) => v.trim() !== "" || "Enter your last name." })} />
          )}
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field id="enr-email" label="Email" required error={e.email && e.email.message}>
          {(a) => (
            <input {...a} type="email" autoComplete="email" className="input-brutal" data-testid="field-email"
              {...register("email", {
                required: "Enter your email address.",
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address." },
              })} />
          )}
        </Field>
        <Field id="enr-phone" label="Phone" required error={e.phone && e.phone.message}>
          {(a) => (
            <input {...a} type="tel" autoComplete="tel" className="input-brutal" data-testid="field-phone"
              {...register("phone", {
                required: "Enter your phone number.",
                validate: (v) => v.replace(/\D/g, "").length >= 10 || "Enter a valid phone number.",
              })} />
          )}
        </Field>
      </div>

      {/* Optional SMS consent sits next to the phone field, unchecked, separate from enrollment consents.
          Box and row sized to the same 24px/44px minimum as the required consents (F12), and rendered at
          body size rather than text-xs (F13). */}
      {consents.sms && (
      <div className="border border-[#E2E8F0] rounded-xl p-3" data-testid="consent-sms-block">
        <p className="text-overline text-zinc-500 mb-2">Optional</p>
        <div className="flex items-start gap-3 min-h-[44px]">
          <input id="enr-consent_sms" type="checkbox" className="mt-1 w-6 h-6 shrink-0" data-testid="consent-sms"
            {...register("consent_sms")} />
          <label htmlFor="enr-consent_sms" className="text-base text-zinc-700">
            <ConsentText rendered={consents.sms} />
          </label>
        </div>
      </div>
      )}

      <fieldset className="space-y-5">
        <legend className="text-overline text-zinc-500 mb-2">Service address</legend>
        <Field id="enr-street" label="Street address" required error={addr.street && addr.street.message}>
          {(a) => (
            <input {...a} type="text" autoComplete="address-line1" className="input-brutal" data-testid="field-street"
              {...register("service_address.street", { required: "Enter the service street address." })} />
          )}
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field id="enr-unit" label="Unit" error={addr.unit && addr.unit.message}>
            {(a) => (
              <input {...a} type="text" autoComplete="address-line2" className="input-brutal" data-testid="field-unit"
                {...register("service_address.unit")} />
            )}
          </Field>
          <Field id="enr-city" label="City" required error={addr.city && addr.city.message}>
            {(a) => (
              <input {...a} type="text" autoComplete="address-level2" className="input-brutal" data-testid="field-city"
                {...register("service_address.city", { required: "Enter the service city." })} />
            )}
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <span className="text-overline text-zinc-500 mb-1 block">State</span>
            <p className="input-brutal bg-zinc-50" data-testid="field-state">{stateEntry.name}</p>
          </div>
          <Field id="enr-zip" label="ZIP code" required error={addr.zip && addr.zip.message}>
            {(a) => (
              <input {...a} type="text" inputMode="numeric" autoComplete="postal-code" maxLength={5} className="input-brutal" data-testid="field-zip"
                {...register("service_address.zip", {
                  required: "Enter the 5-digit ZIP code.",
                  pattern: { value: /^\d{5}$/, message: "Enter a 5-digit ZIP code." },
                })} />
            )}
          </Field>
        </div>
      </fieldset>

      <Field id="enr-start" label="Preferred start date" error={e.preferred_start_date && e.preferred_start_date.message}>
        {(a) => (
          <input {...a} type="date" className="input-brutal" data-testid="field-preferred_start_date"
            {...register("preferred_start_date")} />
        )}
      </Field>

      {/* Connector-declared extension fields: the ONLY part of this form that varies by supplier. */}
      {safeExtensionFields.length > 0 && (
        <fieldset className="space-y-5" data-testid="extension-fields">
          <legend className="text-overline text-zinc-500 mb-2">Additional information required by {provider && provider.name}</legend>
          {safeExtensionFields.map((f) => {
            const id = `enr-ext-${f.name}`;
            const err = extErr[f.name] && extErr[f.name].message;
            const rules = {};
            if (f.required) rules.required = `${f.label} is required.`;
            if (f.pattern) rules.pattern = { value: new RegExp(f.pattern), message: f.pattern_message || `Enter a valid ${f.label.toLowerCase()}.` };
            return (
              <Field key={f.name} id={id} label={f.label} required={!!f.required} hint={isEsidField(f) ? esidFieldHelp(!!f.required) : f.help} error={err}>
                {(a) =>
                  f.type === "select" ? (
                    <select {...a} className="input-brutal" data-testid={`extension-field-${f.name}`}
                      {...register(`supplier_extension.${f.name}`, rules)}>
                      <option value="">Select…</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input {...a} type={f.type || "text"} maxLength={f.maxLength} className="input-brutal" data-testid={`extension-field-${f.name}`}
                      {...register(`supplier_extension.${f.name}`, rules)} />
                  )
                }
              </Field>
            );
          })}
        </fieldset>
      )}

      {/* Disclosures (Design Specification Section 10.4), shown open and in full before the agreements below,
          not only behind a link. "About our service" (broker identity block) is omitted until Martha-BRM and
          Witherspoon-CLO populate a real registered name (Footer.jsx follows the same rule: never render a
          broker-identity claim before registration exists). */}
      <div className="space-y-3 border-t border-zinc-200 pt-5">
        <p className="text-overline text-zinc-500">Before you agree</p>
        <CompensationDisclosure showScope={false} testId="enroll-compensation-disclosure" />
        <div className="bg-[#E8EEF6] rounded-xl p-4 text-sm text-[#0A2540]" data-testid="enroll-what-happens">
          <p className="font-semibold mb-1">What happens when you enroll</p>
          <p>You are choosing {provider && provider.name ? provider.name : "the selected provider"} as your retail electric provider. {WHAT_HAPPENS_WHEN_YOU_ENROLL_LEAD}</p>
        </div>
      </div>

      <fieldset className="space-y-4 border-t border-zinc-200 pt-5">
        <legend className="text-overline text-zinc-500 mb-2">Agreements</legend>
        <p className="text-sm text-zinc-500 -mt-2">All four are required to enroll.</p>
        <div>
          <div className="flex items-start gap-3 min-h-[44px]">
            <input id="enr-consent_tos" type="checkbox" className="mt-1 w-6 h-6 shrink-0" data-testid="consent-tos"
              aria-invalid={e.consent_tos ? "true" : "false"} aria-describedby={e.consent_tos ? "enr-consent_tos-error" : undefined}
              {...register("consent_tos", { required: "You must agree to the Terms of Use and Privacy Policy to continue." })} />
            <label htmlFor="enr-consent_tos" className="text-base">
              <ConsentText rendered={consents.tos} />
            </label>
          </div>
          {e.consent_tos && <p id="enr-consent_tos-error" role="alert" className="text-sm text-red-700 mt-1">{e.consent_tos.message}</p>}
        </div>
        <div>
          <div className="flex items-start gap-3 min-h-[44px]">
            <input id="enr-consent_data_share" type="checkbox" className="mt-1 w-6 h-6 shrink-0" data-testid="consent-data_share"
              aria-invalid={e.consent_data_share ? "true" : "false"} aria-describedby={e.consent_data_share ? "enr-consent_data_share-error" : undefined}
              {...register("consent_data_share", { required: "You must authorize sharing your information with the provider to enroll." })} />
            <label htmlFor="enr-consent_data_share" className="text-base">
              <ConsentText rendered={consents.data_share} />
            </label>
          </div>
          {e.consent_data_share && <p id="enr-consent_data_share-error" role="alert" className="text-sm text-red-700 mt-1">{e.consent_data_share.message}</p>}
        </div>
        <div>
          <div className="flex items-start gap-3 min-h-[44px]">
            <input id="enr-consent_authority" type="checkbox" className="mt-1 w-6 h-6 shrink-0" data-testid="consent-authority"
              aria-invalid={e.consent_authority ? "true" : "false"}
              {...register("consent_authority", { required: "Please confirm you are authorized to change the provider for this address." })} />
            <label htmlFor="enr-consent_authority" className="text-base">{ACK_TEXTS.authority}</label>
          </div>
          {e.consent_authority && <p role="alert" className="text-sm text-red-700 mt-1">{e.consent_authority.message}</p>}
        </div>
        <div>
          {/* Document links, directly above the acknowledgment checkbox (Section 10.5): the visitor can open
              them before checking the box that says they reviewed them. */}
          <DocumentLinks plan={plan} planName={plan && plan.name} variant="row" className="mb-2" testId="enroll-doc-links" />
          <div className="flex items-start gap-3 min-h-[44px]">
            <input id="enr-consent_docs" type="checkbox" className="mt-1 w-6 h-6 shrink-0" data-testid="consent-docs"
              aria-invalid={e.consent_docs ? "true" : "false"}
              {...register("consent_docs", { required: "Please confirm you reviewed the plan documents." })} />
            <label htmlFor="enr-consent_docs" className="text-base">{ACK_TEXTS.supplier_docs}</label>
          </div>
          {e.consent_docs && <p role="alert" className="text-sm text-red-700 mt-1">{e.consent_docs.message}</p>}
        </div>
      </fieldset>

      {rateReview && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="rate-review">
          <div className="flex items-start gap-3 min-h-[44px]">
            <input id="enr-consent_rate" type="checkbox" className="mt-1 w-6 h-6 shrink-0" data-testid="consent-rate"
              aria-invalid={e.consent_rate ? "true" : "false"} aria-describedby={e.consent_rate ? "enr-consent_rate-error" : undefined}
              {...register("consent_rate", { validate: (v) => v === true || "Please confirm you reviewed the updated rate." })} />
            <label htmlFor="enr-consent_rate" className="text-base">
              I reviewed the updated rate{plan && Number.isFinite(plan.rate_1000) ? ` (${plan.rate_1000.toFixed(1)}¢ per kWh at 1,000 kWh)` : ""} and the plan documents, and I want to continue.
            </label>
          </div>
          {e.consent_rate && <p id="enr-consent_rate-error" role="alert" className="text-sm text-red-700 mt-1">{e.consent_rate.message}</p>}
        </div>
      )}

      <button type="submit" disabled={submitting} aria-busy={submitting} className="btn-green w-full" data-testid="enroll-submit">
        {submitLabel} {!submitting && <ArrowRight className="w-5 h-5" />}
      </button>
    </form>
  );
}
